import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  getAllPendingApprovals,
  approveInstance,
  rejectInstance,
  type PendingApproval,
} from '../../api/workflowApi.js';

// Every pending approval in the tenant, so whoever oversees approvals can see
// where each request is waiting. Seeing it is not deciding it: the engine lets
// only the person (or role, or a permitted delegate) the step is routed to
// decide it, so a row offers Approve/Reject only when the server says this
// reader may act on it. Everything else is shown with whom it waits on.

const PAGE = 25;

/**
 * Who the step is waiting on, in words. A step routed to a role carries no
 * user, so the role code is the honest answer there; a step routed to a person
 * names the person. The raw userId is never shown — a uuid tells the reader
 * nothing he can act on.
 */
function waitingOn(r: PendingApproval): string {
  if (r.assignedUserName) {
    return r.assignedLoginId
      ? `${r.assignedUserName} (${r.assignedLoginId})`
      : r.assignedUserName;
  }
  return r.assignedRoleCode ?? r.assignedLoginId ?? 'Unassigned';
}

export function AllApprovalsPage(): ReactNode {
  const { token } = useAuth();
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<PendingApproval[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // window.prompt is unavailable in embedded and mobile web views, so the
  // decision is collected in the page itself.
  const [pending, setPending] = useState<{
    row: PendingApproval;
    decision: 'APPROVE' | 'REJECT';
  } | null>(null);
  const [remarksText, setRemarksText] = useState('');

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await getAllPendingApprovals(token, {
        page,
        pageSize: PAGE,
        overdueOnly,
      });
      setRows(r.data ?? []);
      setTotal(r.meta?.total ?? 0);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, page, overdueOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (row: PendingApproval, decision: 'APPROVE' | 'REJECT', remarks: string) => {
      if (decision === 'REJECT' && !remarks.trim()) return;

      setBusyId(row.instanceStepId);
      setNotice(null);
      try {
        if (decision === 'APPROVE') {
          await approveInstance(token, row.instanceId, remarks.trim() || undefined);
        } else {
          await rejectInstance(token, row.instanceId, remarks.trim());
        }
        setNotice(
          `${row.instanceRefNumber ?? '—'} ${
            decision === 'APPROVE' ? 'approved' : 'rejected'
          }.`,
        );
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyId(null);
        setPending(null);
        setRemarksText('');
      }
    },
    [token, load],
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE));

  return (
    <main aria-label="all-approvals">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>All Approvals</h1>
      <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--color-text-muted)' }}>
        Every request awaiting a decision in this tenant, whoever it is assigned to.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            data-testid="overdue-filter"
            checked={overdueOnly}
            onChange={(e) => {
              setPage(1);
              setOverdueOnly(e.target.checked);
            }}
          />
          Overdue only
        </label>
        <button type="button" onClick={() => void load()} data-testid="refresh">
          Refresh
        </button>
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          {total} pending
        </span>
      </div>

      {error && (
        <div role="alert" data-testid="approvals-error" style={{ color: 'var(--color-danger, #d32f2f)', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div role="status" data-testid="approvals-notice" style={{ marginBottom: 12 }}>
          {notice}
        </div>
      )}

      {pending && (
        <div
          role="dialog"
          aria-label="approval-decision"
          data-testid="approval-decision"
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--color-border-default)',
            background: 'var(--color-bg-elevated)',
          }}
        >
          <p style={{ marginTop: 0, fontSize: 13 }}>
            {pending.decision === 'APPROVE' ? 'Approve' : 'Reject'}{' '}
            <strong>{pending.row.instanceRefNumber ?? '—'}</strong>
          </p>
          <label htmlFor="approval-remarks" style={{ fontSize: 12 }}>
            {pending.decision === 'APPROVE'
              ? 'Remarks (optional)'
              : 'Reason for rejection (required)'}
          </label>
          <textarea
            id="approval-remarks"
            data-testid="approval-remarks"
            rows={3}
            value={remarksText}
            onChange={(e) => setRemarksText(e.target.value)}
            style={{ width: '100%', marginTop: 4 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              data-testid="approval-confirm"
              disabled={
                busyId === pending.row.instanceStepId ||
                (pending.decision === 'REJECT' && !remarksText.trim())
              }
              onClick={() => void act(pending.row, pending.decision, remarksText)}
            >
              Confirm
            </button>
            <button
              type="button"
              data-testid="approval-cancel"
              onClick={() => {
                setPending(null);
                setRemarksText('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows === null ? (
        <p data-testid="approvals-loading">Loading…</p>
      ) : rows.length === 0 ? (
        <p data-testid="approvals-empty">Nothing is waiting for a decision.</p>
      ) : (
        <table data-testid="approvals-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Reference</th>
              <th style={{ textAlign: 'left' }}>Type</th>
              <th style={{ textAlign: 'left' }}>Step</th>
              <th style={{ textAlign: 'left' }}>Assigned to</th>
              <th style={{ textAlign: 'left' }}>Waiting since</th>
              <th style={{ textAlign: 'left' }}>Due</th>
              <th style={{ textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.instanceStepId} data-testid={`approval-row-${r.instanceStepId}`}>
                <td>
                  <Link to={`/admin/wf/instances/${r.instanceId}`}>
                    {r.instanceRefNumber ?? '—'}
                  </Link>
                </td>
                <td>{r.entityType ?? '—'}</td>
                <td>{r.stepSequence}</td>
                <td>{waitingOn(r)}</td>
                <td>{at(r.assignedAt)}</td>
                <td style={r.isOverdue ? { color: 'var(--color-danger, #d32f2f)' } : undefined}>
                  {at(r.dueAt)}
                  {r.isOverdue ? ' (overdue)' : ''}
                </td>
                <td style={{ display: 'flex', gap: 6 }}>
                  {r.canAct ? (
                    <>
                      <button
                        type="button"
                        disabled={busyId === r.instanceStepId}
                        data-testid={`approve-${r.instanceStepId}`}
                        onClick={() => { setPending({ row: r, decision: 'APPROVE' }); setRemarksText(''); }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={busyId === r.instanceStepId}
                        data-testid={`reject-${r.instanceStepId}`}
                        onClick={() => { setPending({ row: r, decision: 'REJECT' }); setRemarksText(''); }}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span
                      data-testid={`not-yours-${r.instanceStepId}`}
                      style={{ fontSize: 12, color: 'var(--color-text-muted)' }}
                    >
                      Waiting on {waitingOn(r)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pageCount > 1 && (
        <nav aria-label="approvals-pagination" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span style={{ fontSize: 13 }}>
            Page {page} of {pageCount}
          </span>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </nav>
      )}
    </main>
  );
}
